import React, { useEffect, useRef } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import Select, { components as selectComponents, OptionProps, MultiValueProps, ValueContainerProps, MultiValue, ActionMeta } from 'react-select';
import WhoopPuckAttribution from '@/components/shared/WhoopPuckAttribution';
import GarminAttribution from '@/components/shared/GarminAttribution';
import CombinedAttribution from '@/components/shared/CombinedAttribution';
import { CHART_COLORS } from '@/utils/chartColors';
import { generateDateRange, formatDateLabel } from '@/utils/dateUtils';

// Define proper interfaces for better type safety
interface AthleteData {
    athlete_id: number;
    athlete_name: string;
    whoop_connected: boolean;

    // Enhanced Recovery Data
    recovery_data?: Array<{
        date: string;
        recovery_score: number;
        hrv_rmssd_milli?: number;
        resting_heart_rate?: number;
        respiratory_rate?: number;
        skin_temp_celsius?: number;
        training_readiness?: string;
        created_at?: string;
    }>;

    // Enhanced Sleep Data with sleep architecture
    sleep_data?: Array<{
        date: string;
        total_duration_hours: number;
        sleep_quality_score?: number;
        sleep_consistency_percentage?: number;
        sleep_efficiency?: number;
        deep_sleep_hours?: number;
        light_sleep_hours?: number;
        rem_sleep_hours?: number;
        awake_duration_hours?: number;
        respiratory_rate?: number;
        sleep_needed_baseline_hours?: number;
        disturbances?: number;
        is_nap?: boolean;
    }>;

    // Enhanced Activity Data with detailed strain metrics
    activity_data?: Array<{
        date: string;
        activity_type: string;
        strain_score?: number;
        duration_minutes: number;
        calories_burned?: number;
        average_heart_rate?: number;
        max_heart_rate?: number;
        kilojoule?: number;
        distance_meter?: number;
        altitude_gain_meter?: number;
        zone_duration?: {
            zone_1_minutes?: number;
            zone_2_minutes?: number;
            zone_3_minutes?: number;
            zone_4_minutes?: number;
            zone_5_minutes?: number;
        };
        perceived_exertion?: number;
        sport_id?: number;
    }>;

    stats?: {
        average_recovery?: number;
        latest_recovery?: number;
        recovery_trend?: number;
        average_hrv?: number;
        latest_hrv?: number;
        hrv_trend?: number;
        average_sleep_quality?: number;
        latest_sleep_quality?: number;
        average_sleep_duration?: number;
        average_sleep_efficiency?: number;
        average_deep_sleep?: number;
        average_rem_sleep?: number;
        average_strain?: number;
        latest_strain?: number;
        total_strain?: number;
        average_hr?: number;
        max_hr_peak?: number;
        data_points?: number;
        last_sync?: string;
    };
}

interface TeamData {
    // Time-series data for charts (raw daily values)
    // Note: For aggregate statistics (average HRV, average recovery, etc.),
    // use readiness_data.all_athletes which provides consistent calculations
    // from ReadinessCalculatorV2 across all dashboard components.
    recovery_data?: {
        summary: {
            total_athletes: number;
            athletes_with_data: number;
            date_range_days: number;
        };
        athletes_data: AthleteData[];
    };
    // Unified readiness data (single source of truth for aggregate metrics)
    readiness_data?: {
        generated_at: string;
        algorithm_version: string;
        all_athletes: Array<{
            id: number;
            name: string;
            score: number;
            tier: string;
            hrv: { value: number; trend: number; average: number; latest: number; consistency: number };
            recovery: { average: number; latest: number; trend: number; data_points: number };
            sleep: { average_duration: number; latest_duration: number; average_quality: number; latest_quality: number; average_efficiency: number; data_points: number };
            strain: { average: number; latest: number; total: number; data_points: number };
        }>;
        summary: { total_athletes: number; optimal: number; moderate: number; low: number; athletes_with_data: number };
    };
    sleep_data?: {
        summary: {
            total_athletes: number;
            athletes_with_data: number;
            date_range_days: number;
        };
        athletes_data: AthleteData[];
    };
    activity_data?: {
        summary: {
            total_athletes: number;
            athletes_with_data: number;
            date_range_days: number;
        };
        athletes_data: AthleteData[];
    };
    garmin_data?: {
        team_name: string;
        athletes_data: Array<{
            athlete_id: number;
            athlete_name: string;
            garmin_connected: boolean;
            whoop_connected: boolean;
            distance_data: Array<{ date: string; distance_miles: number }>;
            rhr_data: Array<{ date: string; resting_hr: number; whoop_rhr?: number; source?: string }>;
            whoop_rhr_data: Array<{ date: string; resting_hr: number; source: string }>;
            weekly_data: Array<{
                week_start_date: string;
                weekly_total: number;
                days_count: number;
                rolling_average?: number;
                weeks_in_window?: number;
            }>;
            hr_zone_data?: Array<{
                date: string;
                zone_1_minutes: number;
                zone_2_minutes: number;
                zone_3_minutes: number;
                zone_4_minutes: number;
                zone_5_minutes: number;
                total_minutes: number;
            }>;
            hr_zone_summary?: {
                avg_zone_1: number;
                avg_zone_2: number;
                avg_zone_3: number;
                avg_zone_4: number;
                avg_zone_5: number;
                days_with_data: number;
            };
            sleep_efficiency_data?: Array<{
                date: string;
                efficiency: number;
                score?: number;
                source: string;
            }>;
            sleep_stages_data?: Array<{
                date: string;
                light_sleep_hours: number;
                deep_sleep_hours: number;
                rem_sleep_hours: number;
            }>;
            stats: {
                average_distance_miles: number;
                total_distance_miles: number;
                average_rhr: number;
                latest_rhr: number;
                baseline_rhr: number | null;
                distance_data_points: number;
                rhr_data_points: number;
                average_sleep_efficiency?: number;
                sleep_efficiency_data_points?: number;
            };
        }>;
        team_weekly_stats?: Array<{
            week_start_date: string;
            team_mean: number;
            team_std_dev: number;
            team_upper_bound: number;
            team_lower_bound: number;
            athletes_count: number;
        }>;
        summary: {
            total_athletes: number;
            athletes_with_data: number;
            date_range_days: number;
        };
    };
    error?: string;
}

interface ChartData {
    labels: string[];
    datasets: Array<{
        label: string;
        data: Array<number | null>;
        borderColor: string;
        backgroundColor: string;
        [key: string]: any;
    }>;
}

interface ChartOptions {
    responsive: boolean;
    maintainAspectRatio: boolean;
    [key: string]: any;
}

interface SelectOption {
    value: string;
    label: string;
}

export interface AnalyticsDashboardTabProps {
    selectedTeam: TeamData;
    isDark: boolean;
    days: number;
    selectedRecoveryAthletes: string[];
    selectedSleepAthletes: string[];
    selectedStrainAthletes: string[];
    setSelectedRecoveryAthletes: (athletes: string[]) => void;
    setSelectedSleepAthletes: (athletes: string[]) => void;
    setSelectedStrainAthletes: (athletes: string[]) => void;
    recoveryMenuOpen: boolean;
    sleepMenuOpen: boolean;
    strainMenuOpen: boolean;
    setRecoveryMenuOpen: (open: boolean) => void;
    setSleepMenuOpen: (open: boolean) => void;
    setStrainMenuOpen: (open: boolean) => void;
    generateFilteredRecoveryChartData: (teamData: TeamData['recovery_data']) => ChartData;
    generateFilteredSleepChartData: (teamData: TeamData['sleep_data']) => ChartData;
    generateFilteredActivityChartData: (teamData: TeamData['activity_data']) => ChartData;
    getMetricChartOptions: (metricType: 'recovery' | 'sleep' | 'strain') => ChartOptions;
    getSleepChartOptions: () => ChartOptions;
    getAthleteLabel: (athlete: AthleteData) => string;
    selectStyles: Record<string, any>;
    CheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    HiddenMultiValue: React.ComponentType<MultiValueProps<SelectOption, true>>;
    RecoveryValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    SleepValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    StrainValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    RecoveryMenuWithSelectAll: React.ComponentType<any>;
    SleepMenuWithSelectAll: React.ComponentType<any>;
    StrainMenuWithSelectAll: React.ComponentType<any>;
    RecoveryCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    SleepCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    StrainCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    focusedRecoveryAthlete: string | null;
    focusedSleepAthlete: string | null;
    focusedStrainAthlete: string | null;
    reorderOptions: (options: SelectOption[], focusedAthlete: string | null) => SelectOption[];
    selectedDataSources: string[];
    // HR Zone filter props
    selectedHRZoneAthletes: string[];
    setSelectedHRZoneAthletes: (athletes: string[]) => void;
    hrZoneMenuOpen: boolean;
    setHRZoneMenuOpen: (open: boolean) => void;
    HRZoneValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    HRZoneMenuWithSelectAll: React.ComponentType<any>;
    HRZoneCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    focusedHRZoneAthlete: string | null;
    // Zone Trend filter props
    selectedZoneTrendAthletes: string[];
    setSelectedZoneTrendAthletes: (athletes: string[]) => void;
    zoneTrendMenuOpen: boolean;
    setZoneTrendMenuOpen: (open: boolean) => void;
    ZoneTrendValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    ZoneTrendMenuWithSelectAll: React.ComponentType<any>;
    ZoneTrendCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    focusedZoneTrendAthlete: string | null;
    // RHR filter props
    selectedRHRAthletes: string[];
    setSelectedRHRAthletes: (athletes: string[]) => void;
    rhrMenuOpen: boolean;
    setRHRMenuOpen: (open: boolean) => void;
    RHRValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    RHRMenuWithSelectAll: React.ComponentType<any>;
    RHRCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    focusedRHRAthlete: string | null;
    setFocusedRHRAthlete: (athlete: string | null) => void;
    // Sleep Stages filter props
    selectedSleepStagesAthletes: string[];
    setSelectedSleepStagesAthletes: (athletes: string[]) => void;
    sleepStagesMenuOpen: boolean;
    setSleepStagesMenuOpen: (open: boolean) => void;
    SleepStagesValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    SleepStagesMenuWithSelectAll: React.ComponentType<any>;
    SleepStagesCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    focusedSleepStagesAthlete: string | null;
    // Daily Mileage filter props
    selectedDailyMileageAthletes: string[];
    setSelectedDailyMileageAthletes: (athletes: string[]) => void;
    dailyMileageMenuOpen: boolean;
    setDailyMileageMenuOpen: (open: boolean) => void;
    DailyMileageValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    DailyMileageMenuWithSelectAll: React.ComponentType<any>;
    DailyMileageCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    focusedDailyMileageAthlete: string | null;
    setFocusedDailyMileageAthlete: (athlete: string | null) => void;
    // Weekly Mileage filter props
    selectedWeeklyMileageAthletes: string[];
    setSelectedWeeklyMileageAthletes: (athletes: string[]) => void;
    weeklyMileageMenuOpen: boolean;
    setWeeklyMileageMenuOpen: (open: boolean) => void;
    WeeklyMileageValueContainer: React.ComponentType<ValueContainerProps<SelectOption, true>>;
    WeeklyMileageMenuWithSelectAll: React.ComponentType<any>;
    WeeklyMileageCheckboxOption: React.ComponentType<OptionProps<SelectOption, true>>;
    focusedWeeklyMileageAthlete: string | null;
    setFocusedWeeklyMileageAthlete: (athlete: string | null) => void;
}

const AnalyticsDashboardTab: React.FC<AnalyticsDashboardTabProps> = ({
    selectedTeam,
    isDark,
    days,
    selectedRecoveryAthletes,
    selectedSleepAthletes,
    selectedStrainAthletes,
    setSelectedRecoveryAthletes,
    setSelectedSleepAthletes,
    setSelectedStrainAthletes,
    recoveryMenuOpen,
    sleepMenuOpen,
    strainMenuOpen,
    setRecoveryMenuOpen,
    setSleepMenuOpen,
    setStrainMenuOpen,
    generateFilteredRecoveryChartData,
    generateFilteredSleepChartData,
    generateFilteredActivityChartData,
    getMetricChartOptions,
    getSleepChartOptions,
    getAthleteLabel,
    selectStyles,
    CheckboxOption,
    HiddenMultiValue,
    RecoveryValueContainer,
    SleepValueContainer,
    StrainValueContainer,
    RecoveryMenuWithSelectAll,
    SleepMenuWithSelectAll,
    StrainMenuWithSelectAll,
    RecoveryCheckboxOption,
    SleepCheckboxOption,
    StrainCheckboxOption,
    focusedRecoveryAthlete,
    focusedSleepAthlete,
    focusedStrainAthlete,
    reorderOptions,
    selectedDataSources,
    // New filter props
    selectedHRZoneAthletes,
    setSelectedHRZoneAthletes,
    hrZoneMenuOpen,
    setHRZoneMenuOpen,
    HRZoneValueContainer,
    HRZoneMenuWithSelectAll,
    HRZoneCheckboxOption,
    focusedHRZoneAthlete,
    selectedZoneTrendAthletes,
    setSelectedZoneTrendAthletes,
    zoneTrendMenuOpen,
    setZoneTrendMenuOpen,
    ZoneTrendValueContainer,
    ZoneTrendMenuWithSelectAll,
    ZoneTrendCheckboxOption,
    focusedZoneTrendAthlete,
    selectedRHRAthletes,
    setSelectedRHRAthletes,
    rhrMenuOpen,
    setRHRMenuOpen,
    RHRValueContainer,
    RHRMenuWithSelectAll,
    RHRCheckboxOption,
    focusedRHRAthlete,
    setFocusedRHRAthlete,
    selectedSleepStagesAthletes,
    setSelectedSleepStagesAthletes,
    sleepStagesMenuOpen,
    setSleepStagesMenuOpen,
    SleepStagesValueContainer,
    SleepStagesMenuWithSelectAll,
    SleepStagesCheckboxOption,
    focusedSleepStagesAthlete,
    // Daily Mileage filter props
    selectedDailyMileageAthletes,
    setSelectedDailyMileageAthletes,
    dailyMileageMenuOpen,
    setDailyMileageMenuOpen,
    DailyMileageValueContainer,
    DailyMileageMenuWithSelectAll,
    DailyMileageCheckboxOption,
    focusedDailyMileageAthlete,
    setFocusedDailyMileageAthlete,
    // Weekly Mileage filter props
    selectedWeeklyMileageAthletes,
    setSelectedWeeklyMileageAthletes,
    weeklyMileageMenuOpen,
    setWeeklyMileageMenuOpen,
    WeeklyMileageValueContainer,
    WeeklyMileageMenuWithSelectAll,
    WeeklyMileageCheckboxOption,
    focusedWeeklyMileageAthlete,
    setFocusedWeeklyMileageAthlete,
}) => {
    const recoveryRef = useRef<HTMLDivElement | null>(null);
    const sleepRef = useRef<HTMLDivElement | null>(null);
    const strainRef = useRef<HTMLDivElement | null>(null);
    const hrZoneRef = useRef<HTMLDivElement | null>(null);
    const zoneTrendRef = useRef<HTMLDivElement | null>(null);
    const rhrRef = useRef<HTMLDivElement | null>(null);
    const sleepStagesRef = useRef<HTMLDivElement | null>(null);
    const dailyMileageRef = useRef<HTMLDivElement | null>(null);
    const weeklyMileageRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const isInsideAnyMenu = (target: Node | null): boolean => {
            if (!target) return false;
            if (!(target instanceof Element)) return false;
            return !!target.closest('.react-select__menu');
        };

        const handleOutside = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node | null;
            const outsideRecovery = recoveryRef.current ? !recoveryRef.current.contains(target as Node) : true;
            const outsideSleep = sleepRef.current ? !sleepRef.current.contains(target as Node) : true;
            const outsideStrain = strainRef.current ? !strainRef.current.contains(target as Node) : true;
            const outsideHRZone = hrZoneRef.current ? !hrZoneRef.current.contains(target as Node) : true;
            const outsideZoneTrend = zoneTrendRef.current ? !zoneTrendRef.current.contains(target as Node) : true;
            const outsideRHR = rhrRef.current ? !rhrRef.current.contains(target as Node) : true;
            const outsideSleepStages = sleepStagesRef.current ? !sleepStagesRef.current.contains(target as Node) : true;
            const outsideDailyMileage = dailyMileageRef.current ? !dailyMileageRef.current.contains(target as Node) : true;
            const outsideWeeklyMileage = weeklyMileageRef.current ? !weeklyMileageRef.current.contains(target as Node) : true;

            if (isInsideAnyMenu(target)) return;

            if (recoveryMenuOpen && outsideRecovery) setRecoveryMenuOpen(false);
            if (sleepMenuOpen && outsideSleep) setSleepMenuOpen(false);
            if (strainMenuOpen && outsideStrain) setStrainMenuOpen(false);
            if (hrZoneMenuOpen && outsideHRZone) setHRZoneMenuOpen(false);
            if (zoneTrendMenuOpen && outsideZoneTrend) setZoneTrendMenuOpen(false);
            if (rhrMenuOpen && outsideRHR) setRHRMenuOpen(false);
            if (sleepStagesMenuOpen && outsideSleepStages) setSleepStagesMenuOpen(false);
            if (dailyMileageMenuOpen && outsideDailyMileage) setDailyMileageMenuOpen(false);
            if (weeklyMileageMenuOpen && outsideWeeklyMileage) setWeeklyMileageMenuOpen(false);
        };

        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
    }, [recoveryMenuOpen, sleepMenuOpen, strainMenuOpen, hrZoneMenuOpen, zoneTrendMenuOpen, rhrMenuOpen, sleepStagesMenuOpen,
        dailyMileageMenuOpen, weeklyMileageMenuOpen,
        setRecoveryMenuOpen, setSleepMenuOpen, setStrainMenuOpen, setHRZoneMenuOpen, setZoneTrendMenuOpen, setRHRMenuOpen,
        setSleepStagesMenuOpen, setDailyMileageMenuOpen, setWeeklyMileageMenuOpen]);
    return (
        <div className="space-y-8">
            {/* ==================== COMBINED SOURCE GRAPHS ==================== */}

            {/* Sleep Efficiency Graph - Combined Data Source (supports both Garmin and WHOOP data) */}
            {(
                (selectedDataSources.includes('garmin') && selectedTeam.garmin_data?.athletes_data.some(a => a.sleep_efficiency_data && a.sleep_efficiency_data.length > 0)) ||
                (selectedDataSources.includes('whoop') && selectedTeam.sleep_data?.athletes_data.some(a => a.sleep_data?.some(s => s.sleep_efficiency !== undefined)))
            ) && (
                <div className={`cgm-card relative rounded-2xl p-8`}>
                    <div ref={rhrRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                        <Select<SelectOption, true>
                            isMulti
                            isSearchable={true}
                            options={reorderOptions(
                                (() => {
                                    // Get athletes with sleep efficiency data from either source
                                    const athleteNames = new Set<string>();
                                    if (selectedDataSources.includes('garmin')) {
                                        selectedTeam.garmin_data?.athletes_data
                                            .filter(a => a.sleep_efficiency_data && a.sleep_efficiency_data.length > 0)
                                            .forEach(a => athleteNames.add(a.athlete_name));
                                    }
                                    if (selectedDataSources.includes('whoop')) {
                                        selectedTeam.sleep_data?.athletes_data
                                            .filter(a => a.sleep_data?.some(s => s.sleep_efficiency !== undefined))
                                            .forEach(a => athleteNames.add(a.athlete_name));
                                    }
                                    return Array.from(athleteNames).map(name => {
                                        const garminAthlete = selectedTeam.garmin_data?.athletes_data.find(a => a.athlete_name === name);
                                        const whoopAthlete = selectedTeam.sleep_data?.athletes_data.find(a => a.athlete_name === name);
                                        const athlete = garminAthlete || whoopAthlete;
                                        return { value: name, label: athlete ? getAthleteLabel(athlete as unknown as AthleteData) : name };
                                    });
                                })(),
                                focusedRHRAthlete
                            )}
                            value={rhrMenuOpen
                                ? selectedRHRAthletes.map(name => {
                                    const garminAthlete = selectedTeam.garmin_data?.athletes_data.find((a) => a.athlete_name === name);
                                    const whoopAthlete = selectedTeam.sleep_data?.athletes_data.find((a) => a.athlete_name === name);
                                    const athlete = garminAthlete || whoopAthlete;
                                    return { value: name, label: athlete ? getAthleteLabel(athlete as unknown as AthleteData) : name };
                                })
                                : []}
                            onChange={(newValue: MultiValue<SelectOption>) =>
                                setSelectedRHRAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                            }
                            classNamePrefix="react-select"
                            placeholder={(() => {
                                const athleteNames = new Set<string>();
                                if (selectedDataSources.includes('garmin')) {
                                    selectedTeam.garmin_data?.athletes_data
                                        .filter(a => a.sleep_efficiency_data && a.sleep_efficiency_data.length > 0)
                                        .forEach(a => athleteNames.add(a.athlete_name));
                                }
                                if (selectedDataSources.includes('whoop')) {
                                    selectedTeam.sleep_data?.athletes_data
                                        .filter(a => a.sleep_data?.some(s => s.sleep_efficiency !== undefined))
                                        .forEach(a => athleteNames.add(a.athlete_name));
                                }
                                return `Showing ${selectedRHRAthletes.length} of ${athleteNames.size} athletes`;
                            })()}
                            menuIsOpen={rhrMenuOpen}
                            onMenuOpen={() => setRHRMenuOpen(true)}
                            onMenuClose={() => setRHRMenuOpen(false)}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            styles={selectStyles}
                            components={{ Option: RHRCheckboxOption, MultiValue: HiddenMultiValue, ValueContainer: RHRValueContainer, Menu: RHRMenuWithSelectAll, ClearIndicator: undefined }}
                        />
                    </div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex flex-col">
                            <h3 className="text-2xl font-bold theme-text-primary">Sleep Efficiency</h3>
                            <p className="theme-text-secondary">Daily sleep efficiency from connected devices</p>
                        </div>
                    </div>
                    <div className="flex flex-col h-96 mb-6">
                        <Line
                            data={(() => {
                                // Use consistent date range for all charts
                                const dateLabels = generateDateRange(days);
                                const athleteDatasets: any[] = [];
                                let colorIndex = 0;

                                // Build a map of athlete name -> date -> { garmin?, whoop? }
                                const athleteDataMap = new Map<string, Map<string, { garmin?: number; whoop?: number }>>();

                                // Collect Garmin data
                                if (selectedDataSources.includes('garmin') && selectedTeam.garmin_data) {
                                    selectedTeam.garmin_data.athletes_data.forEach(athlete => {
                                        if (!selectedRHRAthletes.includes(athlete.athlete_name)) return;
                                        if (!athlete.sleep_efficiency_data || athlete.sleep_efficiency_data.length === 0) return;

                                        if (!athleteDataMap.has(athlete.athlete_name)) {
                                            athleteDataMap.set(athlete.athlete_name, new Map());
                                        }
                                        const dateMap = athleteDataMap.get(athlete.athlete_name)!;

                                        athlete.sleep_efficiency_data.forEach(d => {
                                            // Shift sleep date forward by 1 day to show under the day you wake up
                                            const sleepDate = new Date(d.date + 'T00:00:00Z');
                                            sleepDate.setUTCDate(sleepDate.getUTCDate() + 1);
                                            const displayDate = sleepDate.toISOString().split('T')[0];

                                            const existing = dateMap.get(displayDate) || {};
                                            // Normalize: if value < 2, treat as decimal (0-1) and convert to percentage
                                            // Real sleep efficiency is typically 70-95%, so values < 2 indicate decimal format
                                            const efficiency = d.efficiency < 2
                                                ? d.efficiency * 100
                                                : d.efficiency;
                                            dateMap.set(displayDate, { ...existing, garmin: efficiency });
                                        });
                                    });
                                }

                                // Collect WHOOP data
                                if (selectedDataSources.includes('whoop') && selectedTeam.sleep_data) {
                                    selectedTeam.sleep_data.athletes_data.forEach(athlete => {
                                        if (!selectedRHRAthletes.includes(athlete.athlete_name)) return;
                                        if (!athlete.sleep_data?.some(s => s.sleep_efficiency !== undefined)) return;

                                        if (!athleteDataMap.has(athlete.athlete_name)) {
                                            athleteDataMap.set(athlete.athlete_name, new Map());
                                        }
                                        const dateMap = athleteDataMap.get(athlete.athlete_name)!;

                                        athlete.sleep_data?.forEach(d => {
                                            if (d.sleep_efficiency !== undefined) {
                                                // Shift sleep date forward by 1 day to show under the day you wake up
                                                const sleepDate = new Date(d.date + 'T00:00:00Z');
                                                sleepDate.setUTCDate(sleepDate.getUTCDate() + 1);
                                                const displayDate = sleepDate.toISOString().split('T')[0];

                                                const existing = dateMap.get(displayDate) || {};
                                                // Normalize: if value < 2, treat as decimal (0-1) and convert to percentage
                                                // Real sleep efficiency is typically 70-95%, so values < 2 indicate decimal format
                                                const efficiency = d.sleep_efficiency < 2
                                                    ? d.sleep_efficiency * 100
                                                    : d.sleep_efficiency;
                                                dateMap.set(displayDate, { ...existing, whoop: efficiency });
                                            }
                                        });
                                    });
                                }

                                // Create averaged datasets per athlete
                                athleteDataMap.forEach((dateMap, athleteName) => {
                                    const color = CHART_COLORS[colorIndex % CHART_COLORS.length];
                                    colorIndex++;
                                    const isSelected = focusedRHRAthlete === athleteName;
                                    const isDimmed = focusedRHRAthlete !== null && !isSelected;

                                    const data = dateLabels.map(date => {
                                        const entry = dateMap.get(date);
                                        if (!entry) return null;
                                        const values: number[] = [];
                                        if (entry.garmin !== undefined) values.push(entry.garmin);
                                        if (entry.whoop !== undefined) values.push(entry.whoop);
                                        if (values.length === 0) return null;
                                        // Average the values from both sources
                                        return values.reduce((a, b) => a + b, 0) / values.length;
                                    });

                                    athleteDatasets.push({
                                        label: athleteName,
                                        data,
                                        borderColor: isDimmed ? '#9CA3AF' : color,
                                        backgroundColor: isDimmed ? '#9CA3AF' : color,
                                        borderWidth: isSelected ? 4 : isDimmed ? 2 : 3,
                                        pointBackgroundColor: isDimmed ? '#9CA3AF' : color,
                                        pointBorderColor: '#ffffff',
                                        pointBorderWidth: 2,
                                        pointRadius: isSelected ? 5 : isDimmed ? 3 : 4,
                                        pointHoverRadius: isSelected ? 7 : isDimmed ? 0 : 6,
                                        pointHitRadius: isDimmed ? 0 : 10,
                                        fill: false,
                                        tension: 0.4,
                                        spanGaps: true,
                                        borderDash: isDimmed ? [5, 5] : [],
                                        order: isSelected ? 1 : isDimmed ? 3 : 2, // Selected on top, dimmed behind
                                    });
                                });

                                if (athleteDatasets.length === 0) {
                                    return { labels: [], datasets: [] };
                                }

                                // Add threshold lines
                                const optimalLine = {
                                    label: 'Optimal (90%)',
                                    data: dateLabels.map(() => 90),
                                    borderColor: isDark ? '#10B981' : '#059669',
                                    backgroundColor: 'transparent',
                                    borderWidth: 1,
                                    borderDash: [5, 5],
                                    pointRadius: 0,
                                    fill: false,
                                };
                                const adequateLine = {
                                    label: 'Adequate (80%)',
                                    data: dateLabels.map(() => 80),
                                    borderColor: isDark ? '#F59E0B' : '#D97706',
                                    backgroundColor: 'transparent',
                                    borderWidth: 1,
                                    borderDash: [5, 5],
                                    pointRadius: 0,
                                    fill: false,
                                };
                                const needsImprovementLine = {
                                    label: 'Needs Improvement (70%)',
                                    data: dateLabels.map(() => 70),
                                    borderColor: isDark ? '#EF4444' : '#DC2626',
                                    backgroundColor: 'transparent',
                                    borderWidth: 1,
                                    borderDash: [5, 5],
                                    pointRadius: 0,
                                    fill: false,
                                };

                                return {
                                    labels: dateLabels.map(d => formatDateLabel(d)),
                                    datasets: [...athleteDatasets, optimalLine, adequateLine, needsImprovementLine],
                                };
                            })()}
                            options={{
                                ...getMetricChartOptions('recovery'),
                                interaction: {
                                    intersect: false,
                                    mode: 'index' as const,
                                },
                                onHover: (event: any, elements: any[], chart: any) => {
                                    const canvas = chart.canvas;
                                    if (elements.length > 0) {
                                        // Find the closest element to the mouse position
                                        const mouseY = event.native?.offsetY ?? event.y;
                                        let closestElement = elements[0];
                                        let minDistance = Infinity;

                                        elements.forEach((el: any) => {
                                            const meta = chart.getDatasetMeta(el.datasetIndex);
                                            const point = meta.data[el.index];
                                            if (point) {
                                                const distance = Math.abs(point.y - mouseY);
                                                if (distance < minDistance) {
                                                    minDistance = distance;
                                                    closestElement = el;
                                                }
                                            }
                                        });

                                        const dataset = chart.data.datasets[closestElement.datasetIndex];
                                        const athleteName = dataset.label;
                                        // Skip threshold lines
                                        if (athleteName?.includes('Optimal') || athleteName?.includes('Adequate') || athleteName?.includes('Needs Improvement')) {
                                            canvas.style.cursor = 'default';
                                            return;
                                        }
                                        const isDimmed = focusedRHRAthlete !== null && focusedRHRAthlete !== athleteName;
                                        canvas.style.cursor = isDimmed ? 'not-allowed' : 'pointer';
                                    } else {
                                        canvas.style.cursor = 'default';
                                    }
                                },
                                onClick: (event: any, elements: any[], chart: any) => {
                                    if (elements.length > 0) {
                                        // Find the closest element to the click position
                                        const mouseY = event.native?.offsetY ?? event.y;
                                        let closestElement = elements[0];
                                        let minDistance = Infinity;

                                        elements.forEach((el: any) => {
                                            const meta = chart.getDatasetMeta(el.datasetIndex);
                                            const point = meta.data[el.index];
                                            if (point) {
                                                const distance = Math.abs(point.y - mouseY);
                                                if (distance < minDistance) {
                                                    minDistance = distance;
                                                    closestElement = el;
                                                }
                                            }
                                        });

                                        const dataset = chart.data.datasets[closestElement.datasetIndex];
                                        const clickedAthleteName = dataset.label;

                                        // Skip threshold lines
                                        if (clickedAthleteName?.includes('Optimal') || clickedAthleteName?.includes('Adequate') || clickedAthleteName?.includes('Needs Improvement')) {
                                            return;
                                        }

                                        // Check if clicked athlete is dimmed - if so, ignore completely
                                        const isDimmed = focusedRHRAthlete !== null && focusedRHRAthlete !== clickedAthleteName;
                                        if (isDimmed) {
                                            return; // Ignore clicks on dimmed athletes
                                        }

                                        // Toggle: click on active athlete to deselect, click when nothing selected to select
                                        setFocusedRHRAthlete(focusedRHRAthlete === clickedAthleteName ? null : clickedAthleteName);
                                    }
                                },
                                plugins: {
                                    ...getMetricChartOptions('recovery').plugins,
                                    tooltip: {
                                        ...getMetricChartOptions('recovery').plugins.tooltip,
                                            filter: (tooltipItem: any) => {
                                            // Only show focused athlete in tooltip, or all if none focused
                                            const athleteName = tooltipItem.dataset.label;
                                            // Always show threshold lines
                                            if (athleteName?.includes('Optimal') || athleteName?.includes('Adequate') || athleteName?.includes('Needs Improvement')) {
                                                return true;
                                            }
                                            return focusedRHRAthlete === null || focusedRHRAthlete === athleteName;
                                        },
                                        callbacks: {
                                            ...getMetricChartOptions('recovery').plugins.tooltip.callbacks,
                                            label: (context: any) => {
                                                const value = context.parsed.y;
                                                if (value === null) return '';
                                                const dataset = context.dataset;
                                                // Skip threshold lines in tooltip
                                                if (dataset.label?.includes('Optimal') || dataset.label?.includes('Adequate')) {
                                                    return '';
                                                }
                                                const quality = value >= 90 ? 'Excellent' : value >= 80 ? 'Good' : 'Needs Improvement';
                                                return `${dataset.label}: ${value.toFixed(1)}% - ${quality}`;
                                            },
                                        },
                                    },
                                    legend: {
                                        ...getMetricChartOptions('recovery').plugins.legend,
                                        onClick: (event: any, legendItem: any, legend: any) => {
                                            const clickedAthleteName = legendItem.text;
                                            const chart = legend.chart;
                                            // Skip threshold lines
                                            if (clickedAthleteName.includes('Optimal') || clickedAthleteName.includes('Adequate') || clickedAthleteName.includes('Needs Improvement')) {
                                                const index = legendItem.datasetIndex;
                                                const meta = chart.getDatasetMeta(index);
                                                meta.hidden = !meta.hidden;
                                                chart.update();
                                                return;
                                            }
                                            // Shift+Click or Double-click: toggle focus
                                            if (event.shiftKey || event.detail === 2) {
                                                setFocusedRHRAthlete(focusedRHRAthlete === clickedAthleteName ? null : clickedAthleteName);
                                            } else {
                                                // Single click: toggle visibility
                                                const index = legendItem.datasetIndex;
                                                const meta = chart.getDatasetMeta(index);
                                                meta.hidden = !meta.hidden;
                                                chart.update();
                                            }
                                        },
                                    },
                                    zoom: {
                                        zoom: {
                                            wheel: { enabled: true },
                                            pinch: { enabled: true },
                                            mode: 'x' as const,
                                        },
                                        pan: {
                                            enabled: true,
                                            mode: 'x' as const,
                                        },
                                    },
                                },
                                scales: {
                                    ...getMetricChartOptions('recovery').scales,
                                    y: {
                                        ...getMetricChartOptions('recovery').scales.y,
                                        min: 0,
                                        max: 100,
                                        title: {
                                            display: true,
                                            text: 'Sleep Efficiency (%)',
                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                        },
                                        ticks: {
                                            ...getMetricChartOptions('recovery').scales.y.ticks,
                                            callback: (value: any) => `${value}%`,
                                        },
                                    },
                                },
                            }}
                        />
                        {/* Show attribution based on selected data sources */}
                        {selectedDataSources.includes('garmin') && !selectedDataSources.includes('whoop') && <GarminAttribution />}
                        {selectedDataSources.includes('whoop') && !selectedDataSources.includes('garmin') && <WhoopPuckAttribution />}
                        {selectedDataSources.includes('whoop') && selectedDataSources.includes('garmin') && <CombinedAttribution />}
                    </div>
                </div>
            )}

            {/* Sleep Stages Breakdown - Stacked Bar Chart (supports both WHOOP and Garmin data) */}
            {(selectedDataSources.includes('whoop') || selectedDataSources.includes('garmin')) && (
                // Show if we have WHOOP sleep stage data OR Garmin sleep stages data
                ((selectedDataSources.includes('whoop') && selectedTeam.sleep_data?.athletes_data.some(a => a.sleep_data?.some(s => (s.light_sleep_hours && s.light_sleep_hours > 0) || (s.deep_sleep_hours && s.deep_sleep_hours > 0) || (s.rem_sleep_hours && s.rem_sleep_hours > 0)))) ||
                 (selectedDataSources.includes('garmin') && selectedTeam.garmin_data?.athletes_data?.some(a => a.sleep_stages_data && a.sleep_stages_data.length > 0))) && (
                <div className={`cgm-card relative rounded-2xl p-8`}>
                    <div ref={sleepStagesRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                        <Select<SelectOption, true>
                            isMulti
                            isSearchable={true}
                            options={reorderOptions(
                                (() => {
                                    // Merge athletes from both WHOOP and Garmin sources
                                    const athleteNames = new Set<string>();

                                    // Add WHOOP athletes with sleep stage data
                                    if (selectedDataSources.includes('whoop') && selectedTeam.sleep_data) {
                                        selectedTeam.sleep_data.athletes_data
                                            .filter(a => a.sleep_data?.some(s => (s.light_sleep_hours && s.light_sleep_hours > 0) || (s.deep_sleep_hours && s.deep_sleep_hours > 0) || (s.rem_sleep_hours && s.rem_sleep_hours > 0)))
                                            .forEach(a => athleteNames.add(a.athlete_name));
                                    }

                                    // Add Garmin athletes with sleep stages data
                                    if (selectedDataSources.includes('garmin') && selectedTeam.garmin_data) {
                                        selectedTeam.garmin_data.athletes_data
                                            .filter(a => a.sleep_stages_data && a.sleep_stages_data.length > 0)
                                            .forEach(a => athleteNames.add(a.athlete_name));
                                    }

                                    return Array.from(athleteNames).map(name => {
                                        const whoopAthlete = selectedTeam.sleep_data?.athletes_data.find(a => a.athlete_name === name);
                                        const garminAthlete = selectedTeam.garmin_data?.athletes_data.find(a => a.athlete_name === name);
                                        const athlete = whoopAthlete || garminAthlete;
                                        return { value: name, label: athlete ? getAthleteLabel(athlete as unknown as AthleteData) : name };
                                    });
                                })(),
                                focusedSleepStagesAthlete
                            )}
                            value={sleepStagesMenuOpen
                                ? selectedSleepStagesAthletes.map(name => {
                                    const whoopAthlete = selectedTeam.sleep_data?.athletes_data.find((a: AthleteData) => a.athlete_name === name);
                                    const garminAthlete = selectedTeam.garmin_data?.athletes_data.find(a => a.athlete_name === name);
                                    const athlete = whoopAthlete || garminAthlete;
                                    return { value: name, label: athlete ? getAthleteLabel(athlete as unknown as AthleteData) : name };
                                })
                                : []}
                            onChange={(newValue: MultiValue<SelectOption>) =>
                                setSelectedSleepStagesAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                            }
                            classNamePrefix="react-select"
                            placeholder={(() => {
                                const athleteNames = new Set<string>();
                                if (selectedDataSources.includes('whoop') && selectedTeam.sleep_data) {
                                    selectedTeam.sleep_data.athletes_data
                                        .filter(a => a.sleep_data?.some(s => (s.light_sleep_hours && s.light_sleep_hours > 0) || (s.deep_sleep_hours && s.deep_sleep_hours > 0) || (s.rem_sleep_hours && s.rem_sleep_hours > 0)))
                                        .forEach(a => athleteNames.add(a.athlete_name));
                                }
                                if (selectedDataSources.includes('garmin') && selectedTeam.garmin_data) {
                                    selectedTeam.garmin_data.athletes_data
                                        .filter(a => a.sleep_stages_data && a.sleep_stages_data.length > 0)
                                        .forEach(a => athleteNames.add(a.athlete_name));
                                }
                                return `Showing ${selectedSleepStagesAthletes.length} of ${athleteNames.size} athletes`;
                            })()}
                            menuIsOpen={sleepStagesMenuOpen}
                            onMenuOpen={() => setSleepStagesMenuOpen(true)}
                            onMenuClose={() => setSleepStagesMenuOpen(false)}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            styles={selectStyles}
                            components={{ Option: SleepStagesCheckboxOption, MultiValue: HiddenMultiValue, ValueContainer: SleepStagesValueContainer, Menu: SleepStagesMenuWithSelectAll, ClearIndicator: undefined }}
                        />
                    </div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex flex-col">
                            <h3 className="text-2xl font-bold theme-text-primary">Sleep Stages</h3>
                            <p className="theme-text-secondary">Team sleep architecture breakdown</p>
                        </div>
                    </div>
                    <div className="flex flex-col h-96 mb-6">
                        <Line
                            data={(() => {
                                // Aggregate sleep stage data across selected athletes by date from BOTH sources
                                const dateToStages: Record<string, { light: number; deep: number; rem: number; count: number }> = {};

                                // Process WHOOP sleep data (only athletes with actual sleep stage data)
                                if (selectedDataSources.includes('whoop') && selectedTeam.sleep_data) {
                                    const whoopAthletesWithSleep = selectedTeam.sleep_data.athletes_data.filter(
                                        a => a.sleep_data?.some(s => (s.light_sleep_hours && s.light_sleep_hours > 0) || (s.deep_sleep_hours && s.deep_sleep_hours > 0) || (s.rem_sleep_hours && s.rem_sleep_hours > 0))
                                            && selectedSleepStagesAthletes.includes(a.athlete_name)
                                    );

                                    whoopAthletesWithSleep.forEach(athlete => {
                                        athlete.sleep_data?.forEach(day => {
                                            if (!dateToStages[day.date]) {
                                                dateToStages[day.date] = { light: 0, deep: 0, rem: 0, count: 0 };
                                            }
                                            dateToStages[day.date].light += day.light_sleep_hours || 0;
                                            dateToStages[day.date].deep += day.deep_sleep_hours || 0;
                                            dateToStages[day.date].rem += day.rem_sleep_hours || 0;
                                            dateToStages[day.date].count += 1;
                                        });
                                    });
                                }

                                // Process Garmin sleep stages data
                                if (selectedDataSources.includes('garmin') && selectedTeam.garmin_data) {
                                    const garminAthletesWithSleep = selectedTeam.garmin_data.athletes_data.filter(
                                        a => a.sleep_stages_data && a.sleep_stages_data.length > 0
                                            && selectedSleepStagesAthletes.includes(a.athlete_name)
                                    );

                                    garminAthletesWithSleep.forEach(athlete => {
                                        athlete.sleep_stages_data?.forEach(day => {
                                            if (!dateToStages[day.date]) {
                                                dateToStages[day.date] = { light: 0, deep: 0, rem: 0, count: 0 };
                                            }
                                            dateToStages[day.date].light += day.light_sleep_hours || 0;
                                            dateToStages[day.date].deep += day.deep_sleep_hours || 0;
                                            dateToStages[day.date].rem += day.rem_sleep_hours || 0;
                                            dateToStages[day.date].count += 1;
                                        });
                                    });
                                }

                                // Check if we have any data
                                if (Object.keys(dateToStages).length === 0) {
                                    return { labels: [], datasets: [] };
                                }

                                // Use consistent date range for all charts
                                const dateLabels = generateDateRange(days);

                                return {
                                    labels: dateLabels.map(d => formatDateLabel(d)),
                                    datasets: [
                                        {
                                            label: 'Light Sleep',
                                            data: dateLabels.map(d => {
                                                const dayData = dateToStages[d];
                                                if (!dayData || dayData.count === 0) return 0;
                                                const avg = dayData.light / dayData.count;
                                                return Number(avg.toFixed(2));
                                            }),
                                            backgroundColor: 'rgba(96, 165, 250, 0.7)',
                                            borderColor: '#3B82F6',
                                            borderWidth: 2,
                                            fill: true,
                                            tension: 0.4,
                                            pointRadius: 3,
                                            pointBackgroundColor: '#3B82F6',
                                            pointBorderColor: '#ffffff',
                                            pointBorderWidth: 1,
                                        },
                                        {
                                            label: 'Deep Sleep',
                                            data: dateLabels.map(d => {
                                                const dayData = dateToStages[d];
                                                if (!dayData || dayData.count === 0) return 0;
                                                const avg = dayData.deep / dayData.count;
                                                return Number(avg.toFixed(2));
                                            }),
                                            backgroundColor: 'rgba(52, 211, 153, 0.7)',
                                            borderColor: '#10B981',
                                            borderWidth: 2,
                                            fill: true,
                                            tension: 0.4,
                                            pointRadius: 3,
                                            pointBackgroundColor: '#10B981',
                                            pointBorderColor: '#ffffff',
                                            pointBorderWidth: 1,
                                        },
                                        {
                                            label: 'REM Sleep',
                                            data: dateLabels.map(d => {
                                                const dayData = dateToStages[d];
                                                if (!dayData || dayData.count === 0) return 0;
                                                const avg = dayData.rem / dayData.count;
                                                return Number(avg.toFixed(2));
                                            }),
                                            backgroundColor: 'rgba(244, 114, 182, 0.7)',
                                            borderColor: '#EC4899',
                                            borderWidth: 2,
                                            fill: true,
                                            tension: 0.4,
                                            pointRadius: 3,
                                            pointBackgroundColor: '#EC4899',
                                            pointBorderColor: '#ffffff',
                                            pointBorderWidth: 1,
                                        },
                                    ],
                                };
                            })()}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: {
                                    mode: 'index' as const,
                                    intersect: false,
                                },
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: 'top' as const,
                                        maxWidth: undefined,
                                        maxHeight: 150, // Limit legend height for scrolling
                                        labels: {
                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                            font: {
                                                size: 11, // Slightly smaller for compact view
                                                weight: 500 as const,
                                            },
                                            usePointStyle: true,
                                            pointStyle: 'circle' as const,
                                            padding: 10, // Reduced padding for compact view
                                            boxWidth: 10, // Smaller box for compact view
                                            boxHeight: 10,
                                        },
                                        onClick: (event: any, legendItem: any, legend: any) => {
                                            const clickedDatasetName = legendItem.text;
                                            const chart = legend.chart;
                                            // Single click: toggle visibility (Chart.js default behavior)
                                            const index = legendItem.datasetIndex;
                                            const meta = chart.getDatasetMeta(index);
                                            meta.hidden = !meta.hidden;
                                            chart.update();
                                        },
                                    },
                                    tooltip: {
                                        mode: 'index' as const,
                                        intersect: false,
                                        backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                                        titleColor: isDark ? '#F3F4F6' : '#1F2937',
                                        bodyColor: isDark ? '#F3F4F6' : '#1F2937',
                                        borderColor: isDark ? '#374151' : '#E5E7EB',
                                        borderWidth: 1,
                                        padding: 12,
                                        callbacks: {
                                            label: (context: any) => {
                                                const value = context.parsed.y;
                                                if (value === null || value === 0) return '';
                                                return `${context.dataset.label}: ${value.toFixed(1)} hrs`;
                                            },
                                            footer: (tooltipItems: any) => {
                                                const total = tooltipItems.reduce((sum: number, item: any) => sum + item.parsed.y, 0);
                                                return `Total: ${total.toFixed(1)} hrs`;
                                            },
                                        },
                                    },
                                    zoom: {
                                        zoom: {
                                            wheel: {
                                                enabled: true,
                                            },
                                            pinch: {
                                                enabled: true,
                                            },
                                            mode: 'x' as const,
                                        },
                                        pan: {
                                            enabled: true,
                                            mode: 'x' as const,
                                        },
                                    },
                                },
                                scales: {
                                    x: {
                                        stacked: true,
                                        grid: {
                                            display: true,
                                            color: isDark ? '#374151' : '#F3F4F6',
                                        },
                                        ticks: {
                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                            font: { size: 11 },
                                            maxRotation: 45,
                                            minRotation: 45,
                                        },
                                    },
                                    y: {
                                        stacked: true,
                                        grid: {
                                            display: true,
                                            color: isDark ? '#374151' : '#F3F4F6',
                                        },
                                        ticks: {
                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                            callback: (value: any) => `${value} hrs`,
                                        },
                                        title: {
                                            display: true,
                                            text: 'Average Sleep Duration (hours)',
                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                        },
                                    },
                                },
                            }}
                        />
                        {/* Show attribution based on selected data sources */}
                        {selectedDataSources.includes('garmin') && !selectedDataSources.includes('whoop') && <GarminAttribution />}
                        {selectedDataSources.includes('whoop') && !selectedDataSources.includes('garmin') && <WhoopPuckAttribution />}
                        {selectedDataSources.includes('whoop') && selectedDataSources.includes('garmin') && <CombinedAttribution />}
                    </div>
                </div>
            ))}

            {/* ==================== WHOOP ONLY GRAPHS ==================== */}

            {/* Recovery Chart - WHOOP data */}
            {selectedDataSources.includes('whoop') && (
                selectedTeam.recovery_data && selectedTeam.recovery_data.athletes_data.length > 0 ? (
                <div className={`cgm-card relative rounded-2xl p-8`}>
                    <div ref={recoveryRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                        <Select<SelectOption, true>
                            isMulti
                            isSearchable={true}
                            options={reorderOptions(
                                selectedTeam.recovery_data.athletes_data.map((a: AthleteData) => ({ value: a.athlete_name, label: getAthleteLabel(a) })),
                                focusedRecoveryAthlete
                            )}
                            value={recoveryMenuOpen
                                ? selectedRecoveryAthletes.map(name => {
                                    const athlete = selectedTeam.recovery_data?.athletes_data.find((a: AthleteData) => a.athlete_name === name);
                                    return { value: name, label: athlete ? getAthleteLabel(athlete) : name };
                                })
                                : []}
                            onChange={(newValue: MultiValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) =>
                                setSelectedRecoveryAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                            }
                            classNamePrefix="react-select"
                            placeholder={`Showing ${selectedRecoveryAthletes.length} of ${selectedTeam.recovery_data?.athletes_data.length || 0} athletes`}
                            menuIsOpen={recoveryMenuOpen}
                            onMenuOpen={() => setRecoveryMenuOpen(true)}
                            onMenuClose={() => setRecoveryMenuOpen(false)}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            styles={selectStyles}
                            components={{
                                Option: RecoveryCheckboxOption,
                                MultiValue: HiddenMultiValue,
                                ValueContainer: RecoveryValueContainer,
                                Menu: RecoveryMenuWithSelectAll,
                                ClearIndicator: undefined
                            }}
                        />
                    </div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex flex-col">
                            <h3 className="text-2xl font-bold theme-text-primary">Team Recovery Analysis</h3>
                            <p className="theme-text-secondary">Multi-athlete recovery score trends</p>
                        </div>
                    </div>
                    <div className="flex flex-col h-96 mb-6">
                        <Line
                            data={{
                                ...generateFilteredRecoveryChartData(selectedTeam.recovery_data),
                                labels: generateFilteredRecoveryChartData(selectedTeam.recovery_data).labels.map(l => {
                                    return /^\d{4}-\d{2}-\d{2}$/.test(String(l))
                                        ? new Date(String(l) + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                        : l;
                                })
                            }}
                            options={{
                                ...getMetricChartOptions('recovery'),
                                plugins: {
                                    ...getMetricChartOptions('recovery').plugins,
                                    tooltip: {
                                        ...getMetricChartOptions('recovery').plugins.tooltip,
                                        callbacks: {
                                            ...getMetricChartOptions('recovery').plugins.tooltip.callbacks,
                                            title: (context: any) => formatDateLabel(String(context && context[0] ? context[0].label : '')),
                                        },
                                    },
                                },
                                scales: {
                                    ...getMetricChartOptions('recovery').scales,
                                    x: {
                                        ...getMetricChartOptions('recovery').scales.x,
                                        ticks: {
                                            ...getMetricChartOptions('recovery').scales.x.ticks,
                                            callback: function (this: any, value: any) {
                                                const label = this && typeof this.getLabelForValue === 'function'
                                                    ? this.getLabelForValue(value)
                                                    : String(value);
                                                return formatDateLabel(label);
                                            },
                                        },
                                    },
                                },
                            }}
                        />
                        <WhoopPuckAttribution />
                    </div>
                </div>
            ) : (
                <div className={`cgm-card rounded-2xl p-12 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl text-center`}>
                    <div className={`w-24 h-24 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-bold theme-text-primary mb-4">No Recovery Data Available</h3>
                    <p className="text-lg theme-text-secondary max-w-2xl mx-auto leading-relaxed">
                        {selectedTeam.error || "No athletes in this team have recovery data available for the selected time period."}
                    </p>
                </div>
            ))}

            {/* Sleep Chart - WHOOP data */}
            {selectedDataSources.includes('whoop') && (
                selectedTeam.sleep_data && selectedTeam.sleep_data.athletes_data.length > 0 ? (
                <div className={`cgm-card relative rounded-2xl p-8`}>
                    <div ref={sleepRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                        <Select<SelectOption, true>
                            isMulti
                            isSearchable={true}
                            options={reorderOptions(
                                selectedTeam.sleep_data.athletes_data.map((a: AthleteData) => ({ value: a.athlete_name, label: getAthleteLabel(a) })),
                                focusedSleepAthlete
                            )}
                            value={sleepMenuOpen
                                ? selectedSleepAthletes.map(name => {
                                    const athlete = selectedTeam.sleep_data?.athletes_data.find((a: AthleteData) => a.athlete_name === name);
                                    return { value: name, label: athlete ? getAthleteLabel(athlete) : name };
                                })
                                : []}
                            onChange={(newValue: MultiValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) =>
                                setSelectedSleepAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                            }
                            classNamePrefix="react-select"
                            placeholder={`Showing ${selectedSleepAthletes.length} of ${selectedTeam.sleep_data?.athletes_data.length || 0} athletes`}
                            menuIsOpen={sleepMenuOpen}
                            onMenuOpen={() => setSleepMenuOpen(true)}
                            onMenuClose={() => setSleepMenuOpen(false)}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            styles={selectStyles}
                            components={{ Option: SleepCheckboxOption, MultiValue: HiddenMultiValue, ValueContainer: SleepValueContainer, Menu: SleepMenuWithSelectAll, ClearIndicator: undefined }}
                        />
                    </div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex flex-col">
                            <h3 className="text-2xl font-bold theme-text-primary">Sleep Quality & Duration</h3>
                            <p className="theme-text-secondary">Multi-athlete sleep quality trends</p>
                            <div className="flex items-center space-x-4 mt-2">
                                <div className="flex items-center space-x-2">
                                    <div className="w-4 h-0.5 bg-purple-500 rounded"></div>
                                    <span className="text-xs theme-text-secondary">Sleep Quality</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="w-4 h-0.5 border-dashed border-b-2 border-cyan-500 bg-transparent"></div>
                                    <span className="text-xs theme-text-secondary">Duration (hours)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col h-96 mb-6">
                        <Line
                            data={{
                                ...generateFilteredSleepChartData(selectedTeam.sleep_data),
                                labels: generateFilteredSleepChartData(selectedTeam.sleep_data).labels.map(l => {
                                    return /^\d{4}-\d{2}-\d{2}$/.test(String(l))
                                        ? new Date(String(l) + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                        : l;
                                })
                            }}
                            options={{
                                ...getSleepChartOptions(),
                                plugins: {
                                    ...getSleepChartOptions().plugins,
                                    tooltip: {
                                        ...getSleepChartOptions().plugins.tooltip,
                                        callbacks: {
                                            ...getSleepChartOptions().plugins.tooltip.callbacks,
                                            title: (context: any) => formatDateLabel(String(context && context[0] ? context[0].label : '')),
                                        },
                                    },
                                },
                                scales: {
                                    ...getSleepChartOptions().scales,
                                    x: {
                                        ...getSleepChartOptions().scales.x,
                                        ticks: {
                                            ...getSleepChartOptions().scales.x.ticks,
                                            callback: function (this: any, value: any) {
                                                const label = this && typeof this.getLabelForValue === 'function'
                                                    ? this.getLabelForValue(value)
                                                    : String(value);
                                                return formatDateLabel(label);
                                            },
                                        },
                                    },
                                },
                            }}
                        />
                        <WhoopPuckAttribution />
                    </div>
                </div>
            ) : (
                <div className={`cgm-card rounded-2xl p-12 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl text-center`}>
                    <div className={`w-24 h-24 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-bold theme-text-primary mb-4">No Sleep Data Available</h3>
                    <p className="text-lg theme-text-secondary max-w-2xl mx-auto leading-relaxed">
                        {selectedTeam.error || "No athletes in this team have sleep data available for the selected time period."}
                    </p>
                </div>
            ))}

            {/* Activity/Strain Chart - WHOOP data */}
            {selectedDataSources.includes('whoop') && (
                selectedTeam.activity_data && selectedTeam.activity_data.athletes_data.length > 0 ? (
                <div className={`cgm-card relative rounded-2xl p-8`}>
                    <div ref={strainRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                        <Select<SelectOption, true>
                            isMulti
                            isSearchable={true}
                            options={reorderOptions(
                                selectedTeam.activity_data.athletes_data.map((a: AthleteData) => ({ value: a.athlete_name, label: getAthleteLabel(a) })),
                                focusedStrainAthlete
                            )}
                            value={strainMenuOpen
                                ? selectedStrainAthletes.map(name => {
                                    const athlete = selectedTeam.activity_data?.athletes_data.find((a: AthleteData) => a.athlete_name === name);
                                    return { value: name, label: athlete ? getAthleteLabel(athlete) : name };
                                })
                                : []}
                            onChange={(newValue: MultiValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) =>
                                setSelectedStrainAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                            }
                            classNamePrefix="react-select"
                            placeholder={`Showing ${selectedStrainAthletes.length} of ${selectedTeam.activity_data?.athletes_data.length || 0} athletes`}
                            menuIsOpen={strainMenuOpen}
                            onMenuOpen={() => setStrainMenuOpen(true)}
                            onMenuClose={() => setStrainMenuOpen(false)}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            styles={selectStyles}
                            components={{ Option: StrainCheckboxOption, MultiValue: HiddenMultiValue, ValueContainer: StrainValueContainer, Menu: StrainMenuWithSelectAll, ClearIndicator: undefined }}
                        />
                    </div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex flex-col">
                            <h3 className="text-2xl font-bold theme-text-primary">Team Activity/Strain Analysis</h3>
                            <p className="theme-text-secondary">Multi-athlete daily strain trends</p>
                        </div>
                    </div>
                    <div className="flex flex-col h-96 mb-6">
                        <Line
                            data={{
                                ...generateFilteredActivityChartData(selectedTeam.activity_data),
                                labels: generateFilteredActivityChartData(selectedTeam.activity_data).labels.map(l => {
                                    return /^\d{4}-\d{2}-\d{2}$/.test(String(l))
                                        ? new Date(String(l) + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                        : l;
                                })
                            }}
                            options={{
                                ...getMetricChartOptions('strain'),
                                plugins: {
                                    ...getMetricChartOptions('strain').plugins,
                                    tooltip: {
                                        ...getMetricChartOptions('strain').plugins.tooltip,
                                        callbacks: {
                                            ...getMetricChartOptions('strain').plugins.tooltip.callbacks,
                                            title: (context: any) => formatDateLabel(String(context && context[0] ? context[0].label : '')),
                                        },
                                    },
                                },
                                scales: {
                                    ...getMetricChartOptions('strain').scales,
                                    x: {
                                        ...getMetricChartOptions('strain').scales.x,
                                        ticks: {
                                            ...getMetricChartOptions('strain').scales.x.ticks,
                                            callback: function (this: any, value: any) {
                                                const label = this && typeof this.getLabelForValue === 'function'
                                                    ? this.getLabelForValue(value)
                                                    : String(value);
                                                return formatDateLabel(label);
                                            },
                                        },
                                    },
                                },
                            }}
                        />
                        <WhoopPuckAttribution />
                    </div>
                </div>
            ) : (
                <div className={`cgm-card rounded-2xl p-12 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl text-center`}>
                    <div className={`w-24 h-24 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-bold theme-text-primary mb-4">No Activity Data Available</h3>
                    <p className="text-lg theme-text-secondary max-w-2xl mx-auto leading-relaxed">
                        {selectedTeam.error || "No athletes in this team have activity/strain data available for the selected time period."}
                    </p>
                </div>
            ))}

            {/* Garmin Analytics Graphs */}
            {selectedTeam.garmin_data && selectedTeam.garmin_data.athletes_data.length > 0 && selectedDataSources.includes('garmin') && (
                <>
                    {/* Daily Mileage Graph */}
                    {selectedTeam.garmin_data.athletes_data.some(a => a.distance_data && a.distance_data.length > 0) && (
                        <div className={`cgm-card relative rounded-2xl p-8`}>
                            <div ref={dailyMileageRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                                <Select<SelectOption, true>
                                    isMulti
                                    isSearchable={true}
                                    options={reorderOptions(
                                        selectedTeam.garmin_data.athletes_data
                                            .filter(a => a.distance_data && a.distance_data.length > 0)
                                            .map((a) => ({ value: a.athlete_name, label: getAthleteLabel(a as unknown as AthleteData) })),
                                        focusedDailyMileageAthlete
                                    )}
                                    value={dailyMileageMenuOpen
                                        ? selectedDailyMileageAthletes.map(name => {
                                            const athlete = selectedTeam.garmin_data?.athletes_data.find((a) => a.athlete_name === name);
                                            return { value: name, label: athlete ? getAthleteLabel(athlete as unknown as AthleteData) : name };
                                        })
                                        : []}
                                    onChange={(newValue: MultiValue<SelectOption>) =>
                                        setSelectedDailyMileageAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                                    }
                                    classNamePrefix="react-select"
                                    placeholder={`Showing ${selectedDailyMileageAthletes.length} of ${selectedTeam.garmin_data.athletes_data.filter(a => a.distance_data && a.distance_data.length > 0).length} athletes`}
                                    menuIsOpen={dailyMileageMenuOpen}
                                    onMenuOpen={() => setDailyMileageMenuOpen(true)}
                                    onMenuClose={() => setDailyMileageMenuOpen(false)}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    styles={selectStyles}
                                    components={{ Option: DailyMileageCheckboxOption, MultiValue: HiddenMultiValue, ValueContainer: DailyMileageValueContainer, Menu: DailyMileageMenuWithSelectAll, ClearIndicator: undefined }}
                                />
                            </div>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex flex-col">
                                    <h3 className="text-2xl font-bold theme-text-primary">Daily Mileage</h3>
                                    <p className="theme-text-secondary">Multi-athlete daily distance with team average</p>
                                </div>
                            </div>
                            <div className="flex flex-col h-96 mb-6">
                                <Line
                                    data={(() => {
                                        // Filter athletes based on data source and selection
                                        const filteredAthletes = selectedTeam.garmin_data!.athletes_data.filter(athlete => {
                                            if (!selectedDailyMileageAthletes.includes(athlete.athlete_name)) return false;
                                            if (selectedDataSources.includes('garmin') && athlete.distance_data && athlete.distance_data.length > 0) return true;
                                            return false;
                                        });

                                        if (filteredAthletes.length === 0) {
                                            return { labels: [], datasets: [] };
                                        }

                                        // Use consistent date range for all charts
                                        const dateLabels = generateDateRange(days);

                                        // Build datasets for each athlete
                                        const athleteDatasets = filteredAthletes.map((athlete, index) => {
                                            const color = CHART_COLORS[index % CHART_COLORS.length];
                                            const isSelected = focusedDailyMileageAthlete === athlete.athlete_name;
                                            const isDimmed = focusedDailyMileageAthlete !== null && !isSelected;
                                            const data = dateLabels.map(date => {
                                                const dayData = athlete.distance_data?.find(d => d.date === date);
                                                return dayData ? dayData.distance_miles : null;
                                            });
                                            return {
                                                label: athlete.athlete_name,
                                                data,
                                                borderColor: isDimmed ? '#9CA3AF' : color,
                                                backgroundColor: isDimmed ? '#9CA3AF' : color,
                                                borderWidth: isSelected ? 4 : isDimmed ? 2 : 3,
                                                pointBackgroundColor: isDimmed ? '#9CA3AF' : color,
                                                pointBorderColor: '#ffffff',
                                                pointBorderWidth: 2,
                                                pointRadius: isSelected ? 5 : isDimmed ? 3 : 4,
                                                pointHoverRadius: isSelected ? 7 : isDimmed ? 0 : 6,
                                                pointHitRadius: isDimmed ? 0 : 10,
                                                fill: false,
                                                tension: 0.4,
                                                spanGaps: true,
                                                borderDash: isDimmed ? [5, 5] : [],
                                                order: isSelected ? 1 : isDimmed ? 3 : 2, // Selected on top, dimmed behind
                                            };
                                        });

                                        // Calculate team average
                                        const teamAverageData = dateLabels.map(date => {
                                            const dayValues = filteredAthletes
                                                .map(athlete => athlete.distance_data?.find(d => d.date === date)?.distance_miles)
                                                .filter((v): v is number => v !== null && v !== undefined);
                                            return dayValues.length > 0 ? dayValues.reduce((a, b) => a + b, 0) / dayValues.length : null;
                                        });

                                        return {
                                            labels: dateLabels.map(d => formatDateLabel(d)),
                                            datasets: [
                                                ...athleteDatasets,
                                                {
                                                    label: 'Team Average',
                                                    data: teamAverageData,
                                                    borderColor: isDark ? '#9CA3AF' : '#6B7280',
                                                    backgroundColor: 'transparent',
                                                    borderWidth: 2,
                                                    borderDash: [5, 5],
                                                    pointRadius: 0,
                                                    fill: false,
                                                    tension: 0.4,
                                                },
                                            ],
                                        };
                                    })()}
                                    options={{
                                        ...getMetricChartOptions('recovery'),
                                        plugins: {
                                            ...getMetricChartOptions('recovery').plugins,
                                            tooltip: {
                                                ...getMetricChartOptions('recovery').plugins.tooltip,
                                                callbacks: {
                                                    ...getMetricChartOptions('recovery').plugins.tooltip.callbacks,
                                                    label: (context: any) => {
                                                        const value = context.parsed.y;
                                                        if (value === null) return '';
                                                        return `${context.dataset.label}: ${value.toFixed(2)} mi`;
                                                    },
                                                },
                                            },
                                            legend: {
                                                ...getMetricChartOptions('recovery').plugins.legend,
                                                onClick: (event: any, legendItem: any, legend: any) => {
                                                    const clickedAthleteName = legendItem.text;
                                                    const chart = legend.chart;

                                                    // Shift+Click or Double-click: toggle focus
                                                    if (event.shiftKey || event.detail === 2) {
                                                        setFocusedDailyMileageAthlete(focusedDailyMileageAthlete === clickedAthleteName ? null : clickedAthleteName);
                                                    } else {
                                                        // Single click: toggle visibility
                                                        const index = legendItem.datasetIndex;
                                                        const meta = chart.getDatasetMeta(index);
                                                        meta.hidden = !meta.hidden;
                                                        chart.update();
                                                    }
                                                },
                                            },
                                            zoom: {
                                                zoom: {
                                                    wheel: { enabled: true },
                                                    pinch: { enabled: true },
                                                    mode: 'x' as const,
                                                },
                                                pan: {
                                                    enabled: true,
                                                    mode: 'x' as const,
                                                },
                                            },
                                        },
                                        scales: {
                                            ...getMetricChartOptions('recovery').scales,
                                            y: {
                                                ...getMetricChartOptions('recovery').scales.y,
                                                min: 0,
                                                max: undefined, // Auto-scale based on data
                                                grace: '33%', // Add 33% headroom so max value is ~75% height
                                                title: {
                                                    display: true,
                                                    text: 'Distance (miles)',
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                },
                                                ticks: {
                                                    ...getMetricChartOptions('recovery').scales.y.ticks,
                                                    callback: (value: any) => `${value} mi`,
                                                },
                                            },
                                        },
                                    }}
                                />
                                <GarminAttribution />
                            </div>
                        </div>
                    )}

                    {/* Weekly Rolling Average Graph */}
                    {selectedTeam.garmin_data.team_weekly_stats && selectedTeam.garmin_data.team_weekly_stats.length > 0 && (
                        <div className={`cgm-card relative rounded-2xl p-8`}>
                            <div ref={weeklyMileageRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                                <Select<SelectOption, true>
                                    isMulti
                                    isSearchable={true}
                                    options={reorderOptions(
                                        selectedTeam.garmin_data.athletes_data
                                            .filter(a => a.weekly_data && a.weekly_data.length > 0)
                                            .map((a) => ({ value: a.athlete_name, label: getAthleteLabel(a as unknown as AthleteData) })),
                                        focusedWeeklyMileageAthlete
                                    )}
                                    value={weeklyMileageMenuOpen
                                        ? selectedWeeklyMileageAthletes.map(name => {
                                            const athlete = selectedTeam.garmin_data?.athletes_data.find((a) => a.athlete_name === name);
                                            return { value: name, label: athlete ? getAthleteLabel(athlete as unknown as AthleteData) : name };
                                        })
                                        : []}
                                    onChange={(newValue: MultiValue<SelectOption>) =>
                                        setSelectedWeeklyMileageAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                                    }
                                    classNamePrefix="react-select"
                                    placeholder={`Showing ${selectedWeeklyMileageAthletes.length} of ${selectedTeam.garmin_data.athletes_data.filter(a => a.weekly_data && a.weekly_data.length > 0).length} athletes`}
                                    menuIsOpen={weeklyMileageMenuOpen}
                                    onMenuOpen={() => setWeeklyMileageMenuOpen(true)}
                                    onMenuClose={() => setWeeklyMileageMenuOpen(false)}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    styles={selectStyles}
                                    components={{ Option: WeeklyMileageCheckboxOption, MultiValue: HiddenMultiValue, ValueContainer: WeeklyMileageValueContainer, Menu: WeeklyMileageMenuWithSelectAll, ClearIndicator: undefined }}
                                />
                            </div>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex flex-col">
                                    <h3 className="text-2xl font-bold theme-text-primary">Weekly Mileage Rolling Average</h3>
                                    <p className="theme-text-secondary">4-week smoothed trends with confidence bands</p>
                                </div>
                            </div>
                            <div className="flex flex-col h-96 mb-6">
                                <Line
                                    data={(() => {
                                        const teamStats = selectedTeam.garmin_data!.team_weekly_stats!;
                                        const labels = teamStats.map(s => formatDateLabel(s.week_start_date));

                                        // Get athlete rolling averages - filter by selected athletes
                                        const athleteDatasets = selectedTeam.garmin_data!.athletes_data
                                            .filter(a => a.weekly_data && a.weekly_data.length > 0 && selectedWeeklyMileageAthletes.includes(a.athlete_name))
                                            .map((athlete, index) => {
                                                const color = CHART_COLORS[index % CHART_COLORS.length];
                                                const isSelected = focusedWeeklyMileageAthlete === athlete.athlete_name;
                                                const isDimmed = focusedWeeklyMileageAthlete !== null && !isSelected;
                                                const data = teamStats.map(stat => {
                                                    const athleteWeek = athlete.weekly_data?.find(w => w.week_start_date === stat.week_start_date);
                                                    return athleteWeek?.rolling_average ?? null;
                                                });
                                                return {
                                                    label: athlete.athlete_name,
                                                    data,
                                                    borderColor: isDimmed ? '#9CA3AF' : color,
                                                    backgroundColor: isDimmed ? '#9CA3AF' : color,
                                                    borderWidth: isSelected ? 4 : isDimmed ? 2 : 3,
                                                    pointBackgroundColor: isDimmed ? '#9CA3AF' : color,
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 2,
                                                    pointRadius: isSelected ? 5 : isDimmed ? 3 : 4,
                                                    pointHoverRadius: isSelected ? 7 : isDimmed ? 0 : 6,
                                                    pointHitRadius: isDimmed ? 0 : 10,
                                                    fill: false,
                                                    tension: 0.4,
                                                    spanGaps: true,
                                                    borderDash: isDimmed ? [5, 5] : [],
                                                    order: isSelected ? 1 : isDimmed ? 3 : 2, // Selected on top, dimmed behind
                                                };
                                            });

                                        return {
                                            labels,
                                            datasets: [
                                                {
                                                    label: 'Team Mean',
                                                    data: teamStats.map(s => s.team_mean),
                                                    borderColor: isDark ? '#3B82F6' : '#2563EB',
                                                    backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(37, 99, 235, 0.1)',
                                                    borderWidth: 3,
                                                    pointRadius: 4,
                                                    fill: true,
                                                    tension: 0.4,
                                                },
                                                {
                                                    label: 'Upper Bound',
                                                    data: teamStats.map(s => s.team_upper_bound),
                                                    borderColor: isDark ? '#60A5FA' : '#3B82F6',
                                                    backgroundColor: 'transparent',
                                                    borderWidth: 1,
                                                    borderDash: [2, 2],
                                                    pointRadius: 0,
                                                    fill: false,
                                                    tension: 0.4,
                                                },
                                                {
                                                    label: 'Lower Bound',
                                                    data: teamStats.map(s => s.team_lower_bound),
                                                    borderColor: isDark ? '#60A5FA' : '#3B82F6',
                                                    backgroundColor: 'transparent',
                                                    borderWidth: 1,
                                                    borderDash: [2, 2],
                                                    pointRadius: 0,
                                                    fill: false,
                                                    tension: 0.4,
                                                },
                                                ...athleteDatasets,
                                            ],
                                        };
                                    })()}
                                    options={{
                                        ...getMetricChartOptions('recovery'),
                                        plugins: {
                                            ...getMetricChartOptions('recovery').plugins,
                                            tooltip: {
                                                ...getMetricChartOptions('recovery').plugins.tooltip,
                                                callbacks: {
                                                    ...getMetricChartOptions('recovery').plugins.tooltip.callbacks,
                                                    label: (context: any) => {
                                                        const value = context.parsed.y;
                                                        if (value === null) return '';
                                                        return `${context.dataset.label}: ${value.toFixed(2)} mi/week`;
                                                    },
                                                },
                                            },
                                            legend: {
                                                ...getMetricChartOptions('recovery').plugins.legend,
                                                onClick: (event: any, legendItem: any, legend: any) => {
                                                    const clickedAthleteName = legendItem.text;
                                                    const chart = legend.chart;
                                                    // Skip team stats datasets
                                                    if (clickedAthleteName === 'Team Mean' || clickedAthleteName === 'Upper Bound' || clickedAthleteName === 'Lower Bound') {
                                                        const index = legendItem.datasetIndex;
                                                        const meta = chart.getDatasetMeta(index);
                                                        meta.hidden = !meta.hidden;
                                                        chart.update();
                                                        return;
                                                    }
                                                    // Shift+Click or Double-click: toggle focus
                                                    if (event.shiftKey || event.detail === 2) {
                                                        setFocusedWeeklyMileageAthlete(focusedWeeklyMileageAthlete === clickedAthleteName ? null : clickedAthleteName);
                                                    } else {
                                                        // Single click: toggle visibility
                                                        const index = legendItem.datasetIndex;
                                                        const meta = chart.getDatasetMeta(index);
                                                        meta.hidden = !meta.hidden;
                                                        chart.update();
                                                    }
                                                },
                                            },
                                            zoom: {
                                                zoom: {
                                                    wheel: { enabled: true },
                                                    pinch: { enabled: true },
                                                    mode: 'x' as const,
                                                },
                                                pan: {
                                                    enabled: true,
                                                    mode: 'x' as const,
                                                },
                                            },
                                        },
                                        scales: {
                                            ...getMetricChartOptions('recovery').scales,
                                            y: {
                                                ...getMetricChartOptions('recovery').scales.y,
                                                min: 0,
                                                max: undefined,  // Auto-scale based on data
                                                grace: '20%',    // Add headroom above max value
                                                title: {
                                                    display: true,
                                                    text: 'Weekly Mileage (miles)',
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                },
                                            },
                                        },
                                    }}
                                />
                                <GarminAttribution />
                            </div>
                        </div>
                    )}

                    {/* HR Zone Breakdown - Stacked Bar Chart */}
                    {selectedTeam.garmin_data.athletes_data.some(a =>
                        selectedDataSources.includes('garmin') && a.hr_zone_data && a.hr_zone_data.length > 0
                    ) && (
                        <div className={`cgm-card relative rounded-2xl p-8`}>
                            <div ref={hrZoneRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                                <Select<SelectOption, true>
                                    isMulti
                                    isSearchable={true}
                                    options={reorderOptions(
                                        selectedTeam.garmin_data.athletes_data
                                            .filter(a => a.hr_zone_data && a.hr_zone_data.length > 0)
                                            .map((a) => ({ value: a.athlete_name, label: getAthleteLabel(a as unknown as AthleteData) })),
                                        focusedHRZoneAthlete
                                    )}
                                    value={hrZoneMenuOpen
                                        ? selectedHRZoneAthletes.map(name => {
                                            const athlete = selectedTeam.garmin_data?.athletes_data.find((a) => a.athlete_name === name);
                                            return { value: name, label: athlete ? getAthleteLabel(athlete as unknown as AthleteData) : name };
                                        })
                                        : []}
                                    onChange={(newValue: MultiValue<SelectOption>) =>
                                        setSelectedHRZoneAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                                    }
                                    classNamePrefix="react-select"
                                    placeholder={`Showing ${selectedHRZoneAthletes.length} of ${selectedTeam.garmin_data.athletes_data.filter(a => a.hr_zone_data && a.hr_zone_data.length > 0).length} athletes`}
                                    menuIsOpen={hrZoneMenuOpen}
                                    onMenuOpen={() => setHRZoneMenuOpen(true)}
                                    onMenuClose={() => setHRZoneMenuOpen(false)}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    styles={selectStyles}
                                    components={{ Option: HRZoneCheckboxOption, MultiValue: HiddenMultiValue, ValueContainer: HRZoneValueContainer, Menu: HRZoneMenuWithSelectAll, ClearIndicator: undefined }}
                                />
                            </div>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex flex-col">
                                    <h3 className="text-2xl font-bold theme-text-primary">Time in HR Zones</h3>
                                    <p className="theme-text-secondary">Daily heart rate zone distribution (stacked)</p>
                                    <div className="flex items-center space-x-4 mt-2 flex-wrap gap-y-1">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 rounded" style={{ backgroundColor: isDark ? '#cfcfcf' : '#8a8a8a' }}></div>
                                            <span className="text-xs theme-text-secondary">Z1 Recovery</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }}></div>
                                            <span className="text-xs theme-text-secondary">Z2 Aerobic</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22C55E' }}></div>
                                            <span className="text-xs theme-text-secondary">Z3 Tempo</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F97316' }}></div>
                                            <span className="text-xs theme-text-secondary">Z4 Threshold</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EF4444' }}></div>
                                            <span className="text-xs theme-text-secondary">Z5 VO2max</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col h-96 mb-6">
                                <Bar
                                    data={(() => {
                                        // Aggregate HR zone data across selected athletes by date
                                        const athletesWithZones = selectedTeam.garmin_data!.athletes_data.filter(
                                            a => a.hr_zone_data && a.hr_zone_data.length > 0
                                                && selectedHRZoneAthletes.includes(a.athlete_name)
                                        );

                                        if (athletesWithZones.length === 0) {
                                            return { labels: [], datasets: [] };
                                        }

                                        // Aggregate zone data by date
                                        const dateToZones: Record<string, { z1: number; z2: number; z3: number; z4: number; z5: number }> = {};

                                        athletesWithZones.forEach(athlete => {
                                            athlete.hr_zone_data?.forEach(day => {
                                                if (!dateToZones[day.date]) {
                                                    dateToZones[day.date] = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
                                                }
                                                dateToZones[day.date].z1 += day.zone_1_minutes;
                                                dateToZones[day.date].z2 += day.zone_2_minutes;
                                                dateToZones[day.date].z3 += day.zone_3_minutes;
                                                dateToZones[day.date].z4 += day.zone_4_minutes;
                                                dateToZones[day.date].z5 += day.zone_5_minutes;
                                            });
                                        });

                                        // Use consistent date range for all charts
                                        const dateLabels = generateDateRange(days);

                                        return {
                                            labels: dateLabels.map(d => formatDateLabel(d)),
                                            datasets: [
                                                {
                                                    label: 'Zone 1 (Recovery)',
                                                    data: dateLabels.map(d => {
                                                        const dayData = dateToZones[d];
                                                        return dayData ? Math.round(dayData.z1) : 0;
                                                    }),
                                                    backgroundColor: isDark ? '#cfcfcf' : '#8a8a8a',
                                                    borderColor: isDark ? '#cfcfcf' : '#8a8a8a',
                                                    borderWidth: 1,
                                                    borderRadius: 2,
                                                },
                                                {
                                                    label: 'Zone 2 (Aerobic)',
                                                    data: dateLabels.map(d => {
                                                        const dayData = dateToZones[d];
                                                        return dayData ? Math.round(dayData.z2) : 0;
                                                    }),
                                                    backgroundColor: '#3B82F6',
                                                    borderColor: '#2563EB',
                                                    borderWidth: 1,
                                                    borderRadius: 2,
                                                },
                                                {
                                                    label: 'Zone 3 (Tempo)',
                                                    data: dateLabels.map(d => {
                                                        const dayData = dateToZones[d];
                                                        return dayData ? Math.round(dayData.z3) : 0;
                                                    }),
                                                    backgroundColor: '#22C55E',
                                                    borderColor: '#16A34A',
                                                    borderWidth: 1,
                                                    borderRadius: 2,
                                                },
                                                {
                                                    label: 'Zone 4 (Threshold)',
                                                    data: dateLabels.map(d => {
                                                        const dayData = dateToZones[d];
                                                        return dayData ? Math.round(dayData.z4) : 0;
                                                    }),
                                                    backgroundColor: '#F97316',
                                                    borderColor: '#EA580C',
                                                    borderWidth: 1,
                                                    borderRadius: 2,
                                                },
                                                {
                                                    label: 'Zone 5 (VO2max)',
                                                    data: dateLabels.map(d => {
                                                        const dayData = dateToZones[d];
                                                        return dayData ? Math.round(dayData.z5) : 0;
                                                    }),
                                                    backgroundColor: '#EF4444',
                                                    borderColor: '#DC2626',
                                                    borderWidth: 1,
                                                    borderRadius: 2,
                                                },
                                            ],
                                        };
                                    })()}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        interaction: {
                                            mode: 'index' as const,
                                            intersect: false,
                                        },
                                        plugins: {
                                            legend: {
                                                display: false,
                                            },
                                            tooltip: {
                                                mode: 'index' as const,
                                                intersect: false,
                                                backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                                                titleColor: isDark ? '#F3F4F6' : '#1F2937',
                                                bodyColor: isDark ? '#F3F4F6' : '#1F2937',
                                                borderColor: isDark ? '#374151' : '#E5E7EB',
                                                borderWidth: 1,
                                                padding: 12,
                                                callbacks: {
                                                    label: (context: any) => {
                                                        const value = context.parsed.y;
                                                        if (value === null || value === 0) return '';
                                                        // Calculate total for percentage
                                                        const dataIndex = context.dataIndex;
                                                        const datasets = context.chart.data.datasets;
                                                        const total = datasets.reduce((sum: number, ds: any) => sum + (ds.data[dataIndex] || 0), 0);
                                                        const pct = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                                                        return `${context.dataset.label}: ${value} min (${pct}%)`;
                                                    },
                                                },
                                            },
                                        },
                                        scales: {
                                            x: {
                                                stacked: true,
                                                grid: {
                                                    display: true,
                                                    color: isDark ? '#374151' : '#F3F4F6',
                                                },
                                                ticks: {
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                    font: { size: 11 },
                                                    maxRotation: 45,
                                                    minRotation: 45,
                                                },
                                            },
                                            y: {
                                                stacked: true,
                                                grid: {
                                                    display: true,
                                                    color: isDark ? '#374151' : '#F3F4F6',
                                                },
                                                ticks: {
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                    callback: (value: any) => `${value} min`,
                                                },
                                                title: {
                                                    display: true,
                                                    text: 'Total Team Time (minutes)',
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                },
                                            },
                                        },
                                    }}
                                />
                                <GarminAttribution />
                            </div>
                        </div>
                    )}

                    {/* Zone Trends Over Time - Stacked Bar Chart */}
                    {selectedTeam.garmin_data.athletes_data.some(a =>
                        selectedDataSources.includes('garmin') && a.hr_zone_data && a.hr_zone_data.length > 0
                    ) && (
                        <div className={`cgm-card relative rounded-2xl p-8`}>
                            <div ref={zoneTrendRef} className="relative sm:absolute sm:top-6 sm:right-6 z-10 w-full sm:max-w-xs mb-4 sm:mb-0">
                                <Select<SelectOption, true>
                                    isMulti
                                    isSearchable={true}
                                    options={reorderOptions(
                                        selectedTeam.garmin_data.athletes_data
                                            .filter(a => a.hr_zone_data && a.hr_zone_data.length > 0)
                                            .map((a) => ({ value: a.athlete_name, label: getAthleteLabel(a as unknown as AthleteData) })),
                                        focusedZoneTrendAthlete
                                    )}
                                    value={zoneTrendMenuOpen
                                        ? selectedZoneTrendAthletes.map(name => {
                                            const athlete = selectedTeam.garmin_data?.athletes_data.find((a) => a.athlete_name === name);
                                            return { value: name, label: athlete ? getAthleteLabel(athlete as unknown as AthleteData) : name };
                                        })
                                        : []}
                                    onChange={(newValue: MultiValue<SelectOption>) =>
                                        setSelectedZoneTrendAthletes(newValue ? newValue.map((o: SelectOption) => o.value) : [])
                                    }
                                    classNamePrefix="react-select"
                                    placeholder={`Showing ${selectedZoneTrendAthletes.length} of ${selectedTeam.garmin_data.athletes_data.filter(a => a.hr_zone_data && a.hr_zone_data.length > 0).length} athletes`}
                                    menuIsOpen={zoneTrendMenuOpen}
                                    onMenuOpen={() => setZoneTrendMenuOpen(true)}
                                    onMenuClose={() => setZoneTrendMenuOpen(false)}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    styles={selectStyles}
                                    components={{ Option: ZoneTrendCheckboxOption, MultiValue: HiddenMultiValue, ValueContainer: ZoneTrendValueContainer, Menu: ZoneTrendMenuWithSelectAll, ClearIndicator: undefined }}
                                />
                            </div>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex flex-col">
                                    <h3 className="text-2xl font-bold theme-text-primary">Zone Trends Over Time</h3>
                                    <p className="theme-text-secondary">Weekly team training distribution by HR zone</p>
                                </div>
                            </div>
                            <div className="flex flex-col h-96 mb-6">
                                <Line
                                    data={(() => {
                                        // Aggregate HR zone data by week across selected athletes
                                        const athletesWithZones = selectedTeam.garmin_data!.athletes_data.filter(
                                            a => a.hr_zone_data && a.hr_zone_data.length > 0
                                                && selectedZoneTrendAthletes.includes(a.athlete_name)
                                        );

                                        if (athletesWithZones.length === 0) {
                                            return { labels: [], datasets: [] };
                                        }

                                        // Group by week (using Monday as start of week)
                                        const weekToZones: Record<string, { z1: number; z2: number; z3: number; z4: number; z5: number }> = {};

                                        const getWeekStart = (dateStr: string) => {
                                            const date = new Date(dateStr + 'T00:00:00Z');
                                            const day = date.getUTCDay();
                                            const diff = date.getUTCDate() - day + (day === 0 ? -6 : 1); // Monday
                                            const monday = new Date(date.setUTCDate(diff));
                                            return monday.toISOString().split('T')[0];
                                        };

                                        athletesWithZones.forEach(athlete => {
                                            athlete.hr_zone_data?.forEach(day => {
                                                const weekStart = getWeekStart(day.date);
                                                if (!weekToZones[weekStart]) {
                                                    weekToZones[weekStart] = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
                                                }
                                                // Convert minutes to hours for weekly view
                                                weekToZones[weekStart].z1 += day.zone_1_minutes / 60;
                                                weekToZones[weekStart].z2 += day.zone_2_minutes / 60;
                                                weekToZones[weekStart].z3 += day.zone_3_minutes / 60;
                                                weekToZones[weekStart].z4 += day.zone_4_minutes / 60;
                                                weekToZones[weekStart].z5 += day.zone_5_minutes / 60;
                                            });
                                        });

                                        // Generate weeks that fall within the date range
                                        const dateLabels = generateDateRange(days);
                                        const weekStarts = new Set<string>();
                                        dateLabels.forEach(date => {
                                            weekStarts.add(getWeekStart(date));
                                        });
                                        const sortedWeeks = Array.from(weekStarts).sort();

                                        return {
                                            labels: sortedWeeks.map(d => formatDateLabel(d)),
                                            datasets: [
                                                {
                                                    label: 'Zone 1 (Recovery)',
                                                    data: sortedWeeks.map(w => {
                                                        const weekData = weekToZones[w];
                                                        return weekData ? Number(weekData.z1.toFixed(1)) : 0;
                                                    }),
                                                    backgroundColor: isDark ? 'rgba(207, 207, 207, 0.7)' : 'rgba(138, 138, 138, 0.7)',
                                                    borderColor: isDark ? '#cfcfcf' : '#8a8a8a',
                                                    borderWidth: 2,
                                                    fill: true,
                                                    tension: 0.4,
                                                    pointRadius: 3,
                                                    pointBackgroundColor: isDark ? '#cfcfcf' : '#8a8a8a',
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 1,
                                                },
                                                {
                                                    label: 'Zone 2 (Aerobic)',
                                                    data: sortedWeeks.map(w => {
                                                        const weekData = weekToZones[w];
                                                        return weekData ? Number(weekData.z2.toFixed(1)) : 0;
                                                    }),
                                                    backgroundColor: 'rgba(59, 130, 246, 0.7)',
                                                    borderColor: '#2563EB',
                                                    borderWidth: 2,
                                                    fill: true,
                                                    tension: 0.4,
                                                    pointRadius: 3,
                                                    pointBackgroundColor: '#2563EB',
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 1,
                                                },
                                                {
                                                    label: 'Zone 3 (Tempo)',
                                                    data: sortedWeeks.map(w => {
                                                        const weekData = weekToZones[w];
                                                        return weekData ? Number(weekData.z3.toFixed(1)) : 0;
                                                    }),
                                                    backgroundColor: 'rgba(34, 197, 94, 0.7)',
                                                    borderColor: '#16A34A',
                                                    borderWidth: 2,
                                                    fill: true,
                                                    tension: 0.4,
                                                    pointRadius: 3,
                                                    pointBackgroundColor: '#16A34A',
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 1,
                                                },
                                                {
                                                    label: 'Zone 4 (Threshold)',
                                                    data: sortedWeeks.map(w => {
                                                        const weekData = weekToZones[w];
                                                        return weekData ? Number(weekData.z4.toFixed(1)) : 0;
                                                    }),
                                                    backgroundColor: 'rgba(249, 115, 22, 0.7)',
                                                    borderColor: '#EA580C',
                                                    borderWidth: 2,
                                                    fill: true,
                                                    tension: 0.4,
                                                    pointRadius: 3,
                                                    pointBackgroundColor: '#EA580C',
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 1,
                                                },
                                                {
                                                    label: 'Zone 5 (VO2max)',
                                                    data: sortedWeeks.map(w => {
                                                        const weekData = weekToZones[w];
                                                        return weekData ? Number(weekData.z5.toFixed(1)) : 0;
                                                    }),
                                                    backgroundColor: 'rgba(239, 68, 68, 0.7)',
                                                    borderColor: '#DC2626',
                                                    borderWidth: 2,
                                                    fill: true,
                                                    tension: 0.4,
                                                    pointRadius: 3,
                                                    pointBackgroundColor: '#DC2626',
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 1,
                                                },
                                            ],
                                        };
                                    })()}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        interaction: {
                                            mode: 'index' as const,
                                            intersect: false,
                                        },
                                        plugins: {
                                            legend: {
                                                display: true,
                                                position: 'top' as const,
                                                labels: {
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                    usePointStyle: true,
                                                    pointStyle: 'circle',
                                                    boxWidth: 12,
                                                },
                                            },
                                            tooltip: {
                                                mode: 'index' as const,
                                                intersect: false,
                                                backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                                                titleColor: isDark ? '#F3F4F6' : '#1F2937',
                                                bodyColor: isDark ? '#F3F4F6' : '#1F2937',
                                                borderColor: isDark ? '#374151' : '#E5E7EB',
                                                borderWidth: 1,
                                                padding: 12,
                                                callbacks: {
                                                    label: (context: any) => {
                                                        const value = context.parsed.y;
                                                        if (value === null || value === 0) return '';
                                                        // Calculate total for percentage
                                                        const dataIndex = context.dataIndex;
                                                        const datasets = context.chart.data.datasets;
                                                        const total = datasets.reduce((sum: number, ds: any) => sum + (ds.data[dataIndex] || 0), 0);
                                                        const pct = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                                                        return `${context.dataset.label}: ${value.toFixed(1)} hrs (${pct}%)`;
                                                    },
                                                },
                                            },
                                        },
                                        scales: {
                                            x: {
                                                stacked: true,
                                                grid: {
                                                    display: true,
                                                    color: isDark ? '#374151' : '#F3F4F6',
                                                },
                                                ticks: {
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                    font: { size: 11 },
                                                },
                                            },
                                            y: {
                                                stacked: true,
                                                grid: {
                                                    display: true,
                                                    color: isDark ? '#374151' : '#F3F4F6',
                                                },
                                                ticks: {
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                    callback: (value: any) => `${value} hrs`,
                                                },
                                                title: {
                                                    display: true,
                                                    text: 'Total Team Time (hours)',
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                },
                                            },
                                        },
                                    }}
                                />
                                <GarminAttribution />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AnalyticsDashboardTab;
